import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { style } from './app-tile.style.js';
import getLocalDateFromString from '../../../../utils/get-local-date-from-string.js';
import { DebugService } from '../../../../services/debug/debug.service.js';
import { stratum } from '../../../../constants/stratum.js';
import { HostAppEventsService } from '../../../../utils/host-app-events-service.js';

export const APPLICATION_BUTTON_STATUSES = ['Approved', 'Funding', 'Funded', 'Pending', 'Verifying'];
export const RESUME_APP_STATUSES = ['Approved', 'Funding', 'Pending'];
export const VERIFYING_SUPPORT_PHONE = '888-263-4582';

class AppTileComponent extends LitElement {
  static get styles() {
    return [style];
  }

  constructor() {
    super();
    this.debug = new DebugService('AppTileComponent');
    this.app = null;
    this.imageFailedToLoad = false;
    this.allImagesLoaded = false;
    this.showImage = false;
    this.featureToggles = null;
  }

  static properties = {
    app: { type: Object, attribute: 'app', reflect: true },
    imageFailedToLoad: { type: Boolean, state: true },
    allImagesLoaded: { type: Boolean, attribute: 'all-images-loaded' },
    showImage: { type: Boolean, attribute: 'show-image' },
    featureToggles: { type: Array },
  };

  get isExpired() {
    return !!(this.app?.expirationTimestamp && new Date(this.app.expirationTimestamp) <= new Date());
  }

  // True while a vehicle image is expected but the coordinated reveal hasn't
  // fired yet. The parent waits for ALL tiles' images to resolve before setting
  // all-images-loaded, so tiles reveal together. Drives the skeleton-text state
  // and the reserved image space.
  get isImageLoading() {
    return this.showImage && !!this.app?.vehicleDetails?.imageUrl && !this.imageFailedToLoad && !this.allImagesLoaded;
  }

  isFeatureEnabled(featureKey) {
    if (!this.featureToggles) return false;
    const toggle = this.featureToggles.find(feature => feature.featureKey === featureKey);
    return toggle ? toggle.isEnabled : false;
  }

  // Formats the vehicle details to title case in case of weird capitalization
  formatVehicleDetails() {
    if (!this.app?.vehicleDetails) {
      this.debug.log('No vehicle details available for app:', this.app);
      return 'Your Application';
    }
    const { year, make, model } = this.app?.vehicleDetails;
    // Trim each field and filter out undefined or empty values
    const details = [year?.toString().trim(), make?.trim(), model?.trim()].filter(Boolean);
    if (details.length === 0) {
      this.debug.log('No valid vehicle details available for app:', this.app);
      return 'Your Application';
    }
    let vehicleDetails = details.join(' ');
    // If the combined string length is 30 or greater, omit the model details
    if (vehicleDetails.length >= 30 && details.length === 3) {
      vehicleDetails = [year?.toString().trim(), make?.trim()].filter(Boolean).join(' ');
    }
    // The BFF returns correctly formatted names, so skip regex formatting when it has one to give us.
    // Exception: when imageUrl is missing the vehicle image can't load and the tile falls back to the
    // application-provided (all-caps) name, so always sentence-case those.
    const hasImageUrl = !!this.app?.vehicleDetails?.imageUrl;
    if (hasImageUrl) {
      return vehicleDetails;
    }
    return vehicleDetails.toLowerCase().replace(/(?:^|\s|-)\S/g, match => match.toUpperCase());
  }

  goToApp() {
    this.debug.log('goToApp called with', this.app);
    stratum.setExternalVar('applicationId', `${this.app.applicationId}`);
    const tagKey = this.getButtonText(this.app) === 'Resume application' ? TagKey.PICK_AN_APP_V2_RESUME_APPLICATION_BUTTON : TagKey.PICK_AN_APP_V2_VIEW_DETAILS_BUTTON;
    stratum.publishTag(tagKey, {
      replacements: {
        value: JSON.stringify({
          applicationId: this.app.applicationId,
          applicationStatus: this.app.applicationStatus,
        }),
      },
    });
    HostAppEventsService.sendResumeAppEvent(this.app);
  }

  callSupport() {
    this.debug.log('callSupport called with', this.app);
    stratum.setExternalVar('applicationId', `${this.app.applicationId}`);
    stratum.publishTag(TagKey.PICK_AN_APP_V2_CALL_888_263_4582_BUTTON, {
      replacements: {
        value: JSON.stringify({
          applicationId: this.app.applicationId,
          applicationStatus: this.app.applicationStatus,
        }),
      },
    });
    window.open(`tel:${VERIFYING_SUPPORT_PHONE}`, '_self');
  }

  formatPaymentDate(dateStr) {
    const [year, month, day] = (dateStr || '').split('-');
    if (!year || !month || !day || isNaN(month) || isNaN(day)) return '';
    return `${Number(month)}/${Number(day)}/${year}`;
  }

  // Renders the expiration date row. The label reflects whether the app has already expired
  // ("Expired on:") or is still active ("Expires on:"). When there's no expiration timestamp,
  // falls back to a spacer (or nothing, for expired apps, since a spacer already precedes it).
  renderExpirationDate() {
    const hasExpired = this.isExpired || this.app?.applicationStatus === 'Expired';
    if (!this.app?.expirationTimestamp) {
      return hasExpired ? '' : html`<div class="app-card--spacer"></div>`;
    }
    return html` <div class="app-card--row">
      <fs-heading level="2" size="normal">${hasExpired ? 'Expired on:' : 'Expires on:'}</fs-heading>
      <fs-body-text size="normal"> ${getLocalDateFromString(this.app?.expirationTimestamp)} </fs-body-text>
    </div>`;
  }

  getButtonText() {
    if (this.app?.applicationStatus === 'Verifying') return `Call ${VERIFYING_SUPPORT_PHONE}`;
    return RESUME_APP_STATUSES.includes(this.app?.applicationStatus) ? 'Resume application' : 'View details';
    // TODO: Need to add logic for select offer text once statuses are finalized
  }

  generateButton() {
    const isAppValid = !this.isExpired && APPLICATION_BUTTON_STATUSES.includes(this.app?.applicationStatus);
    const isVerifying = this.app?.applicationStatus === 'Verifying';
    return isAppValid
      ? html`
          <div class="app-card-button">
            <fs-button
              variant="primary"
              type="button"
              @click="${() => {
                isVerifying ? this.callSupport() : this.goToApp();
              }}"
            >
              ${this.getButtonText()}
            </fs-button>
          </div>
        `
      : '';
  }

  renderLoanDetailsBody() {
    if (this.isExpired || this.app?.applicationStatus === 'Expired') {
      return html`
        <fs-body-text>This application has expired. You can start a new application at any time. </fs-body-text>
        <div class="app-card--spacer"></div>
        ${this.renderExpirationDate()}
      `;
    }
    switch (this.app?.applicationStatus) {
      // placeholder until we determine logic for offer not selected
      case 'Approved':
        return html`
          <div class="app-card--row">
            <fs-body-text>Let's pick up where you left off!</fs-body-text>
          </div>
          <div class="app-card--spacer"></div>
          ${this.renderExpirationDate()}
        `;
      case 'Pending':
      case 'Funding':
        return html`
          ${this.app?.selectedOffer && this.app?.selectedOffer?.annualPercentageRate && this.app?.selectedOffer?.loanTermInMonths
            ? html`
                <div class="app-card--row">
                  <fs-heading level="2" size="normal">Selected Offer:</fs-heading>
                  <fs-body-text>${this.app?.selectedOffer?.annualPercentageRate}% • ${Math.trunc(this.app?.selectedOffer?.loanTermInMonths)} months </fs-body-text>
                </div>
              `
            : html`<div class="app-card--spacer"></div>`}
          ${this.renderExpirationDate()}
          <div class="app-card--spacer"></div>
        `;
      case 'Funded':
        return html`
          <fs-body-text>Your loan transfer is moving forward!</fs-body-text>
          <div class="app-card--spacer"></div>
          ${this.formatPaymentDate(this.app?.firstPaymentDate)
            ? html`
              <div class="app-card--row">
                <fs-heading level="2" size="normal">Payment Due:</fs-heading>
                <fs-body-text size="normal"> ${this.formatPaymentDate(this.app.firstPaymentDate)} </fs-body-text>
              </div>`
            : ''}
        `;
      case 'Verifying':
        return html`
          <fs-body-text>We just need a few more details to finalize your application. Give us a quick call and we'll help you wrap things up.</fs-body-text>
          <div class="app-card--spacer"></div>
          ${this.renderExpirationDate()}
        `;
      case 'Received':
      case 'Reviewing':
        return html`
          <fs-body-text>We're reviewing your application. We'll notify you of a decision soon. </fs-body-text>
          <div class="app-card--spacer"></div>
          ${this.renderExpirationDate()}
        `;
      case 'Returned':
        return html` <fs-body-text>This application is now closed. No further action is needed. You can start a new application at any time. </fs-body-text> `;
      case 'Declined':
      case 'Canceled':
      default:
        return html` <fs-body-text>We can't offer any refinancing for this application at this time. </fs-body-text> `;
    }
  }

  renderVehicleImage() {
    if (!this.showImage) return '';
    const imageURL = this.app?.vehicleDetails?.imageUrl;
    if (!imageURL) {
      this.debug.log('Vehicle image not shown: imageUrl is missing', { vehicleDetails: this.app?.vehicleDetails });
      this.dispatchEvent(new CustomEvent('vehicle-image-error', {
        bubbles: true,
        composed: true,
        detail: {
          applicationId: this.app?.applicationId,
          applicationStatus: this.app?.applicationStatus,
          vehicleDetails: this.app?.vehicleDetails,
        },
      }));
      return '';
    }
    if (this.imageFailedToLoad) {
      this.debug.log('Vehicle image not shown: image previously failed to load', { imageURL });
      return '';
    }
    return html`
      <div class="app-card-image">
        <div class="app-card-image--wrapper ${!this.allImagesLoaded ? 'app-card-image--reserved' : ''}">
          <img
            src="${imageURL}"
            alt="${this.formatVehicleDetails()}"
            class="app-card-image--img ${!this.allImagesLoaded ? 'app-card-image--loading' : ''}"
            @load="${this._handleImageLoad}"
            @error="${this._handleImageError}"
          />
        </div>
      </div>
    `;
  }

  _handleImageLoad() {
    this.debug.log('Vehicle image loaded successfully', { imageURL: this.app?.vehicleDetails?.imageUrl, appId: this.app?.applicationId });
    this.dispatchEvent(new CustomEvent('vehicle-image-load-success', { bubbles: true, composed: true }));
  }

  _handleImageError(e) {
    this.debug.log('Vehicle image FAILED to load', {
      imageURL: this.app?.vehicleDetails?.imageUrl,
      appId: this.app?.applicationId,
      errorEvent: e,
      imgElement: e?.target,
      currentSrc: e?.target?.currentSrc,
      naturalWidth: e?.target?.naturalWidth,
    });
    this.imageFailedToLoad = true;
    this.dispatchEvent(new CustomEvent('vehicle-image-error', {
      bubbles: true,
      composed: true,
      detail: {
        applicationId: this.app?.applicationId,
        applicationStatus: this.app?.applicationStatus,
        vehicleDetails: this.app?.vehicleDetails,
      },
    }));
  }

  // Skeleton placeholders shown in place of the tile text while the vehicle image loads.
  renderSkeletonCard() {
    return html`
      <div class="app-card app-card--skeleton" aria-label="Loading application details" aria-busy="true">
        <div class="app-card--header">
          <div class="app-card--row"><span class="skeleton-bar skeleton-bar--title"></span></div>
          <div class="app-card--row ref-id"><span class="skeleton-bar skeleton-bar--subtitle"></span></div>
        </div>
        <div class="skeleton-body">
          <span class="skeleton-bar skeleton-bar--line"></span>
          <span class="skeleton-bar skeleton-bar--line skeleton-bar--line-short"></span>
        </div>
        <div class="app-card--cta"><span class="skeleton-bar skeleton-bar--button"></span></div>
      </div>
    `;
  }

  renderCardContent() {
    return html`
      <div class="app-card" aria-label="Application for ${this.formatVehicleDetails()}, status: ${this.app?.applicationStatus}, reference number ${this.app?.applicationId}">
        <div class="app-card--header">
          <div class="app-card--row"><fs-heading level="3" size="medium">${this.formatVehicleDetails()}</fs-heading></div>
          <div class="app-card--row ref-id"><fs-heading level="3" size="tiny">Reference Number</fs-heading><fs-body-text size="tiny" class="app-card--header-refid">${this.app?.applicationId}</fs-body-text></div>
        </div>
        <div>${this.renderLoanDetailsBody()}</div>
        <div class="app-card--cta">${this.generateButton()}</div>
      </div>
    `;
  }

  render() {
    const hasImage = this.showImage && !!this.app?.vehicleDetails?.imageUrl && !this.imageFailedToLoad;
    return html`
      <div class="app-tile-wrapper">
        ${this.renderVehicleImage()}
        <fs-container elevation="1" radius="large" class="app-card-container ${hasImage ? 'has-image' : ''}">
          ${this.isImageLoading ? this.renderSkeletonCard() : this.renderCardContent()}
        </fs-container>
      </div>
    `;
  }
}

customElements.define('refi-app-tile', AppTileComponent);
